import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Timer, ForkKnife } from "@phosphor-icons/react";
import { toast } from "sonner";

function elapsed(iso) {
  if (!iso) return "0:00";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function timerColor(iso) {
  if (!iso) return "text-zinc-500";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 240) return "text-emerald-600"; // < 4 min
  if (diff < 600) return "text-orange-500"; // < 10 min
  return "text-red-600";
}

export default function Taquero() {
  const { user } = useAuth();
  const station = user?.role || "taquero1";
  const [orders, setOrders] = useState([]);
  const [tick, setTick] = useState(0);

  const load = () => {
    api.get("/orders", { params: { status: "open", station } })
      .then((r) => setOrders(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const t1 = setInterval(load, 5000);
    const t2 = setInterval(() => setTick((x) => x + 1), 1000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [station]);

  const markReady = async (orderId, itemId) => {
    try {
      await api.patch(`/orders/${orderId}/items/${itemId}/ready`);
      toast.success("Item listo ✓");
      load();
    } catch (e) {
      toast.error("Error al marcar listo");
    }
  };

  // Filter out orders where all station items are ready
  const visible = orders.filter((o) => o.items.some((i) => i.status === "pending"));

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-2">Monitor de Cocina</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Estación · {station}</h1>
        </div>
        <div className="flex gap-2 text-xs">
          <div className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{visible.length} órdenes activas</div>
        </div>
      </div>

      {visible.length === 0 && (
        <div className="bg-white rounded-3xl border-2 border-dashed border-zinc-200 py-24 text-center">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-600 items-center justify-center mb-4">
            <Check size={32} weight="bold" />
          </div>
          <div className="font-display text-2xl font-bold">Todo al corriente</div>
          <p className="text-zinc-500 mt-1">No hay productos pendientes en esta estación.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {visible.map((o) => {
          const pendings = o.items.filter((i) => i.status === "pending");
          return (
            <article key={o.id} data-testid={`taquero-order-${o.id}`} className="bg-white rounded-3xl border-2 border-zinc-200 overflow-hidden flex flex-col">
              <header className="bg-zinc-900 text-white p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-400">Orden #{String(o.order_number || 0).padStart(3, "0")}</div>
                  <div className="font-display text-3xl font-black leading-tight">Mesa {o.table_number}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-zinc-400">{o.dine_option === "aqui" ? "Comer aquí" : "Para llevar"}</div>
                  <div className={`font-display text-2xl font-black ${timerColor(o.opened_at)}`}>
                    <Timer size={20} weight="bold" className="inline-block mr-1" />{elapsed(o.opened_at)}
                  </div>
                </div>
              </header>

              <div className="flex-1 p-4 space-y-2">
                {pendings.map((it) => (
                  <button
                    key={it.item_id}
                    onClick={() => markReady(o.id, it.item_id)}
                    data-testid={`taquero-item-${it.item_id}`}
                    className="w-full text-left bg-zinc-50 hover:bg-emerald-50 hover:border-emerald-500 border-2 border-zinc-200 rounded-2xl p-4 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="font-display text-2xl font-black text-brand">×{it.qty}</span>
                          <span className="font-bold text-base">{it.name}</span>
                        </div>
                        {it.variants.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {it.variants.map((v) => (
                              <span key={v} className="text-[11px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-bold uppercase tracking-wider">{v}</span>
                            ))}
                          </div>
                        )}
                        {it.note && <div className="text-xs text-zinc-500 mt-1.5 italic">{it.note}</div>}
                      </div>
                      <div className="w-12 h-12 rounded-xl border-2 border-zinc-300 group-hover:bg-emerald-500 group-hover:border-emerald-500 group-hover:text-white inline-flex items-center justify-center text-zinc-400 transition-colors flex-shrink-0">
                        <Check size={24} weight="bold" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <footer className="px-4 py-3 bg-zinc-50 border-t border-zinc-200 text-xs text-zinc-500 flex justify-between">
                <span>Mesero: {o.mesero_name}</span>
                <span>Toca para marcar listo</span>
              </footer>
            </article>
          );
        })}
      </div>
      <div className="hidden">{tick}</div>
    </div>
  );
}
